import {
  BASE_VARIABLE_MODE_ID,
  getBaseModeName,
  INHERIT_VARIABLE_MODE_ID,
  type VariableCollection,
  type VariableModeId,
} from '@packages/systems/core/utils/StyleVariables';

export type VariableModeEntry = {
  id: VariableModeId;
  name: string;
};

/**
 * Ordered list of selectable mode entries for a variable-mode control:
 * Inherit (when allowed), Base (with display-name override), then
 * non-deleted collection modes filtered by `visibleModeIds`.
 *
 * Callers resolve `showInherit` themselves so they can layer extra gating on
 * top of the authored `VariableModeMeta.showInherit` (e.g. a feature flag).
 */
export const getVariableModeEntries = (
  collection: VariableCollection,
  {
    showInherit = true,
    baseDisplayName,
    visibleModeIds,
  }: {
    showInherit?: boolean;
    baseDisplayName?: string;
    visibleModeIds?: string[];
  } = {}
): VariableModeEntry[] => [
  ...(showInherit ? [{id: INHERIT_VARIABLE_MODE_ID, name: 'Inherit'}] : []),
  {
    id: BASE_VARIABLE_MODE_ID,
    name: getBaseModeName(collection, baseDisplayName),
  },
  ...Object.values(collection.modes)
    // Skip the optional `'base'` metadata entry (used to persist a renamed
    // base mode label) so the synthetic Base row above is not duplicated.
    .filter((mode) => mode.id !== BASE_VARIABLE_MODE_ID)
    .filter((mode) => !mode.deleted)
    .filter((mode) => !visibleModeIds || visibleModeIds.includes(mode.id))
    .map(({id, name}) => ({id, name})),
];
